import { Component, EventEmitter, OnInit, Input, Output } from '@angular/core';
import { CollegeType, ElectionColleges, LineupOptions } from 'src/app/pap-creation/pap-dto/pap-dto';

@Component({
  selector: 'app-one-college',
  templateUrl: './college.component.html',
  styleUrls: ['./college.component.less', '../../../../shared/common.less']
})
export class OneCollegeComponent implements OnInit {
  @Input() college: ElectionColleges;
  @Input() index: number;
  @Input() lineupErrorMessage: string = '';
  @Input() partial: boolean = false;
  @Input() remainingEmployee: number = 0;
  @Output() collegeChange = new EventEmitter<ElectionColleges>();
  totalColleges: number = 1;
  collegeTypeOptions = Object.values(CollegeType);
  lineupOptions = LineupOptions;
  hasCourtDecision: boolean = false;
  womenSeatsCount: number = 0;
  menSeatsCount: number = 0;
  seatsSuggestionChecked: boolean = true;
  substitutesSeatsSuggestionChecked: boolean = true;

  constructor() { }

  onCollegesChange() {
    if (!this.partial) {
      this.college.substituteSeats = this.college.seats;
    }
    
    // Mettre à jour automatiquement les proportions si les checkbox sont cochées
    if (this.seatsSuggestionChecked) {
      this.college.proportion[0] = this.womenSeatsSuggested;
      this.college.proportion[1] = this.menSeatsSuggested;
    }
    
    if (this.substitutesSeatsSuggestionChecked) {
      this.college.substituteProportion[0] = this.womenSubstituteSeatsCount;
      this.college.substituteProportion[1] = this.menSubstituteSeatsCount;
    }
    
    this.collegeChange.emit(this.college);
  }

  onSeatsSuggestionChange() {
    if (this.seatsSuggestionChecked) {
      this.college.proportion[0] = this.womenSeatsSuggested;
      this.college.proportion[1] = this.menSeatsSuggested;
    }
    if (this.substitutesSeatsSuggestionChecked) {
      this.college.substituteProportion[0] = this.womenSubstituteSeatsCount;
      this.college.substituteProportion[1] = this.menSubstituteSeatsCount;
    }
    this.onCollegesChange();
  }

  get cancelledElectionDate(): string {
    if (!this.college.cancelledElection) return '';
    const d = new Date(this.college.cancelledElection);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().substring(0, 10);
  }

  set cancelledElectionDate(value: string) {
    this.college.cancelledElection = value ? new Date(value) : null;
    this.onCollegesChange();
  }

  onCourtDecisionChange() {
    if (!this.hasCourtDecision) {
      this.college.cancelledElection = null;
    }
    this.onCollegesChange();
  }

  isCompositionSelected(option: LineupOptions): boolean {
    return this.college.composition && this.college.composition.includes(option);
  }

  toggleComposition(option: LineupOptions): void {
    if (!this.college.composition) {
      this.college.composition = [];
    }
    const index = this.college.composition.indexOf(option);
    if (index > -1) {
      this.college.composition.splice(index, 1);
    } else {
      this.college.composition.push(option);
    }
    this.onCollegesChange();
  }

  get calculatedMenCount(): number {
    const total = Number(this.college.lineup) || 0;
    const women = Number(this.college.genderDistribution[0]) || 0;
    const men = total - women;
    this.college.genderDistribution[1] = men >= 0 ? men : 0;
    return this.college.genderDistribution[1];
  }

  get hasGenderError(): boolean {
    const total = Number(this.college.lineup) || 0;
    const women = Number(this.college.genderDistribution[0]) || 0;
    return women > total;
  }

  get womenPercentage(): number {
    const total = Number(this.college.lineup) || 0;
    const women = Number(this.college.genderDistribution[0]) || 0;
    if (total === 0) return 0;
    return Math.round((women / total) * 100 * 100) / 100; // Arrondi à 2 décimales
  }

  get menPercentage(): number {
    const total = Number(this.college.lineup) || 0;
    const men = this.calculatedMenCount;
    if (total === 0) return 0;
    return Math.round((men / total) * 100 * 100) / 100; // Arrondi à 2 décimales
  }

  get maxDate(): string {
    return new Date().toISOString().substring(0, 10);
  }

  get womenSeatsCountF(): number {
    // this.womenSeatsCount = Math.round(this.college.seats * (this.womenPercentage / 100));
    // this.college.proportion[0] = this.womenSeatsCount;
    // return this.womenSeatsCount;
    return Math.round(this.college.seats * (this.womenPercentage / 100));
  }

  get womenSeatsSuggested(): number {
    return Math.round(this.college.seats * (this.womenPercentage / 100));
  }

  get menSeatsCountF(): number {
    return Math.round(this.college.seats * (this.menPercentage / 100));
  }

  get menSeatsSuggested(): number {
    return Math.round(this.college.seats * (this.menPercentage / 100));
  }

  get womenSubstituteSeatsCount(): number {
    return Math.round(this.college.substituteSeats * (this.womenPercentage / 100));
  }

  get menSubstituteSeatsCount(): number {
    return Math.round(this.college.substituteSeats * (this.menPercentage / 100));
  }

  get displayedWomenProportion(): number {
    return this.seatsSuggestionChecked ? this.womenSeatsSuggested : this.college.proportion[0];
  }

  get displayedMenProportion(): number {
    return this.seatsSuggestionChecked ? this.menSeatsSuggested : this.college.proportion[1];
  }

  get displayedWomenSubstituteProportion(): number {
    return this.substitutesSeatsSuggestionChecked ? this.womenSubstituteSeatsCount : this.college.substituteProportion[0];
  }

  get displayedMenSubstituteProportion(): number {
    return this.substitutesSeatsSuggestionChecked ? this.menSubstituteSeatsCount : this.college.substituteProportion[1];
  }

  onlyNumbers(event: KeyboardEvent) {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      event.preventDefault();
    }
  }

  ngOnChanges(): void {
    if (!this.partial) {
      this.college.substituteSeats = this.college.seats;
    }
  }

  trackByIndex(index: number): number {
    return index;
  }

  ngOnInit(): void {
    if (!this.college.collegeType[0]) {
      this.college.collegeType[0] = this.collegeTypeOptions[0];
    }
  }

}
